// "agora há pouco" / "2h ago" / "3d geleden", from the locale files.
//
// Two near-identical copies of this lived inside NutritionMiniMirror and
// FitnessMiniMirror with the strings written straight into the TSX — correctly
// translated, but invisible to the i18n layer: nothing counted them, nothing
// checked them, and a new language meant editing components instead of adding
// translations. They also disagreed with each other ("moments ago" in one,
// "just now" in the other, for the same elapsed time).

import type { Lang, TVars } from "@/i18n/locale";
import { localeTagFor } from "@/i18n/locale";

const MINUTE = 60_000;

/**
 * Elapsed-time label for a timestamp.
 *
 * Falls back to an absolute short date past a week — a relative label stops
 * being useful there, and every language formats the date via Intl anyway.
 *
 * @param tf the provider's interpolating translate (t + {{vars}})
 */
export function relativeTimeLabel(
  iso: string,
  lang: Lang,
  tf: (key: string, vars: TVars) => string,
): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";

  const diffMin = Math.floor((Date.now() - then.getTime()) / MINUTE);
  const diffH = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffH / 24);

  if (diffMin < 1) return tf("time.justNow", {});
  if (diffMin < 60) return tf("time.minutesAgo", { n: diffMin });
  if (diffH < 24) return tf("time.hoursAgo", { n: diffH });
  if (diffDays === 1) return tf("time.yesterday", {});
  if (diffDays < 7) return tf("time.daysAgo", { n: diffDays });

  return then.toLocaleDateString(localeTagFor(lang), { day: "2-digit", month: "short" });
}
